<template>
    <div>
        <div :class="contentClass">
            <div class="grid-content align-items-center">
                <div class="grid-item grid-item-quarter">
                    <v-radio
                        class="stack-s"
                        label="Between"
                        :value="operators.between"
                        :model="op"
                        @update="updateCurrentOperator"
                    />
                </div>
                <div class="grid-item inline-s">
                    <v-date-input :value="min"
                                  @input="min = getBOD($event)"
                                  :disabled="!isBetween"
                                  :validate="$v.min"/>
                </div>
                <div class="grid-item no-flex inline-s">and</div>
                <div class="grid-item">
                    <v-date-input :value="max"
                                  @input="max = getEOD($event)"
                                  :disabled="!isBetween"
                                  :validate="$v.max"/>
                </div>
            </div>
            <div class="grid-content align-items-center">
                <div class="grid-item grid-item-quarter">
                    <v-radio
                        class="stack-s"
                        label="Before"
                        :value="operators.lte"
                        :model="op"
                        @update="updateCurrentOperator"
                    />
                </div>
                <div class="grid-item">
                    <v-date-input :value="lowerThan"
                                  @input="lowerThan = getBOD($event)"
                                  :disabled="!isLowerThan"
                                  :validate="$v.lowerThan"/>
                </div>
            </div>
            <div class="grid-content align-items-center">
                <div class="grid-item grid-item-quarter">
                    <v-radio
                        class="stack-s"
                        label="After"
                        :value="operators.gte"
                        :model="op"
                        @update="updateCurrentOperator"
                    />
                </div>
                <div class="grid-item">
                    <v-date-input :value="greaterThan"
                                  @input="greaterThan = getEOD($event)"
                                  :disabled="!isGreaterThan"
                                  :validate="$v.greaterThan"/>
                </div>
            </div>
            <div class="grid-content" v-if="$v.$error">
                <div class="grid-item">
                    <v-error-messages>
                        <li class="list-item" v-if="!$v.min.required">Start date range is required</li>
                        <li class="list-item" v-if="!$v.max.required">End date range is required</li>
                        <li class="list-item" v-if="!$v.max.mustBeGreaterMin">End date must be greater than start date</li>
                        <li class="list-item" v-if="!$v.lowerThan.required">Date is required</li>
                        <li class="list-item" v-if="!$v.greaterThan.required">Date is required</li>
                    </v-error-messages>
                </div>
            </div>
        </div>
        <!-- Footer -->
        <filter-footer
            @apply-filter="applyFilter"
            @cancel-filter="cancelFilter"/>
    </div>
</template>

<script>
    import {vRadio, vErrorMessages, vDateInput} from '@/components/form';
    import FilterFooter from '@/modules/rules-engine/components/condition-builder/filters/filter-footer';
    import BaseFilterMixin from '@/modules/rules-engine/components/condition-builder/filters/base-filter-mixin';
    import ConditionOperators from '@/components/query/spec/condition-operators';
    import CriteriaCondition from '@/components/query/spec/criteria-condition';
    import {requiredIf} from 'vuelidate/lib/validators';

    export default {
        name: 'date-time-filter',
        mixins: [BaseFilterMixin],
        components: {
            vDateInput,
            FilterFooter,
            vErrorMessages,
            vRadio,
        },
        data() {
            return {
                operators: ConditionOperators,
                ...this.getInitialState(),
            };
        },
        computed: {
            isBetween() {
                return this.op === ConditionOperators.between;
            },
            isLowerThan() {
                return this.op === ConditionOperators.lte;
            },
            isGreaterThan() {
                return this.op === ConditionOperators.gte;
            },
        },
        validations: {
            min: {
                required: requiredIf('isBetween'),
            },
            max: {
                required: requiredIf('isBetween'),
                mustBeGreaterMin(date) {
                    return !date || +date > +this.min;
                },
            },
            lowerThan: {
                required: requiredIf('isLowerThan'),
            },
            greaterThan: {
                required: requiredIf('isGreaterThan'),
            },
        },
        methods: {
            getBOD(date) {
                return date.startOf('day');
            },
            getEOD(date) {
                return date.endOf('day');
            },
            updateCurrentOperator(operator) {
                this.op = operator;
                this.min = null;
                this.max = null;
                this.lowerThan = null;
                this.greaterThan = null;
            },
            getInitialState() {
                const {
                    op, min, max, value,
                } = this.condition.data;
                const momentize = date => (date ? this.$tz().getMoment(date) : null);
                return {
                    op: op || ConditionOperators.between,
                    min: momentize(min),
                    max: momentize(max),
                    lowerThan: (op === ConditionOperators.lte && momentize(value)) || null,
                    greaterThan: (op === ConditionOperators.gte && momentize(value)) || null,
                };
            },
            applyFilter() {
                this.$v.$touch();
                if (!this.$v.$error) {
                    const payload = {
                        index: this.index,
                        condition: this.condition,
                    };
                    payload.condition.data = new CriteriaCondition({
                        op: this.op,
                        min: this.min,
                        max: this.max,
                        value: this.lowerThan || this.greaterThan,
                        path: this.condition.getPath(),
                    });
                    this.$emit('apply-filter', payload);
                }
            },
        },
    };
</script>
